<template>
  <div class="slide slide-recommendations">
    <!-- Animated background -->
    <div class="bg-mesh"></div>
    <div class="bg-glow"></div>
    <div class="bg-lines"></div>

    <!-- Header -->
    <div class="slide-header">
      <div class="ai-badge-header">
        <div class="ai-sparkle">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 3L13.4302 8.31181C13.6047 8.93143 14.0686 9.39534 14.6882 9.56981L20 11L14.6882 12.4302C14.0686 12.6047 13.6047 13.0686 13.4302 13.6882L12 19L10.5698 13.6882C10.3953 13.0686 9.93143 12.6047 9.31181 12.4302L4 11L9.31181 9.56981C9.93143 9.39534 10.3953 8.93143 10.5698 8.31181L12 3Z" fill="currentColor"/>
          </svg>
        </div>
        <span>AI-POWERED INSIGHTS</span>
      </div>
      <h2 class="slide-title">{{ data.title || 'Key Recommendations' }}</h2>
      <p class="slide-subtitle">{{ data.subtitle || 'Strategic actions to drive engagement and performance' }}</p>
    </div>

    <!-- Recommendations Grid -->
    <div class="recommendations-grid">
      <div
        v-for="(rec, index) in displayRecommendations"
        :key="index"
        class="rec-card"
        :class="[`priority-${rec.priority}`, `category-${rec.category.toLowerCase().replace(/\s+/g, '-')}`]"
        :style="{ animationDelay: `${index * 0.12}s` }"
      >
        <!-- Priority Ribbon -->
        <div class="priority-ribbon" :class="`ribbon-${rec.priority}`">
          <span class="ribbon-text">{{ getPriorityLabel(rec.priority) }}</span>
        </div>

        <!-- Card Icon -->
        <div class="card-icon" :class="`icon-${rec.category.toLowerCase().replace(/\s+/g, '-')}`">
          <component :is="getCategoryIcon(rec.category)" />
        </div>

        <!-- Card Content -->
        <div class="card-body">
          <div class="card-category">{{ rec.category }}</div>
          <h3 class="card-title">{{ rec.title }}</h3>
          <p class="card-description">{{ rec.description }}</p>
        </div>

        <!-- Card Footer -->
        <div class="card-footer">
          <div class="impact-indicator">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{{ rec.impact || 'High Impact' }}</span>
          </div>
          <div class="metric-pill" v-if="rec.metric">
            {{ rec.metric }}
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="slide-footer">
      <div class="footer-left">
        <div class="ai-signature">
          <div class="signature-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <span>Generated by Happily.ai</span>
        </div>
      </div>
      <div class="footer-right">
        <span class="timestamp" v-if="data.generatedAt">{{ formatTimestamp(data.generatedAt) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// Icon components for categories
const WellbeingIcon = {
  template: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const EngagementIcon = {
  template: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M17 21V19C17 16.79 15.21 15 13 15H5C2.79 15 1 16.79 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2"/>
    <path d="M23 21V19C22.99 17.11 21.79 15.46 20 15.04" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M16 3.04C17.79 3.46 19 5.11 19 7C19 8.89 17.79 10.54 16 10.96" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const CultureIcon = {
  template: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const LeadershipIcon = {
  template: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const DefaultIcon = {
  template: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

// Placeholder recommendations when none provided
const PLACEHOLDER_RECOMMENDATIONS = [
  {
    priority: 'high',
    category: 'Wellbeing',
    title: 'Address Declining WHO-5 Scores',
    description: 'Implement targeted wellness initiatives to support teams showing decreased wellbeing metrics this quarter.',
    impact: 'High Impact',
    metric: 'WHO-5 Index'
  },
  {
    priority: 'high',
    category: 'Engagement',
    title: 'Boost Manager Responsiveness',
    description: 'Enable real-time feedback tools and establish response time expectations to improve manager engagement.',
    impact: 'High Impact',
    metric: 'Response Rate'
  },
  {
    priority: 'medium',
    category: 'Culture',
    title: 'Strengthen Recognition Culture',
    description: 'Launch a peer recognition program to increase visibility of achievements and reinforce company values.',
    impact: 'Medium Impact',
    metric: 'Recognition Rate'
  },
  {
    priority: 'medium',
    category: 'Leadership',
    title: 'Develop Strategic Alignment',
    description: 'Host quarterly all-hands meetings to communicate company vision and align team goals with strategic objectives.',
    impact: 'Medium Impact',
    metric: 'Alignment Score'
  }
];

export default {
  name: 'RecommendationsSlide',
  components: {
    WellbeingIcon,
    EngagementIcon,
    CultureIcon,
    LeadershipIcon,
    DefaultIcon
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  computed: {
    displayRecommendations() {
      // Use provided recommendations or fallback to placeholders
      if (this.data.recommendations && this.data.recommendations.length > 0) {
        return this.data.recommendations.slice(0, 4);
      }
      return PLACEHOLDER_RECOMMENDATIONS;
    }
  },
  methods: {
    getPriorityLabel(priority) {
      const labels = {
        high: 'HIGH PRIORITY',
        medium: 'MEDIUM',
        low: 'LOW'
      };
      return labels[priority] || priority.toUpperCase();
    },
    getCategoryIcon(category) {
      const icons = {
        'wellbeing': 'WellbeingIcon',
        'engagement': 'EngagementIcon',
        'culture': 'CultureIcon',
        'leadership': 'LeadershipIcon'
      };
      return icons[category.toLowerCase()] || 'DefaultIcon';
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-recommendations {
  @extend .slide-base;
  background: linear-gradient(145deg, #0f0f14 0%, #1a1a24 50%, #0f0f14 100%);
  padding: 64px 80px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

// Animated backgrounds
.bg-mesh {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(at 40% 20%, rgba(124, 58, 237, 0.15) 0px, transparent 50%),
    radial-gradient(at 80% 0%, rgba(59, 130, 246, 0.1) 0px, transparent 50%),
    radial-gradient(at 0% 50%, rgba(6, 182, 212, 0.08) 0px, transparent 50%),
    radial-gradient(at 80% 100%, rgba(124, 58, 237, 0.1) 0px, transparent 50%);
  pointer-events: none;
}

.bg-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 800px;
  height: 800px;
  background: radial-gradient(circle, rgba(124, 58, 237, 0.08) 0%, transparent 60%);
  transform: translate(-50%, -50%);
  pointer-events: none;
  animation: pulse 8s ease-in-out infinite;
}

.bg-lines {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px),
    linear-gradient(180deg, rgba(255,255,255,0.02) 1px, transparent 1px);
  background-size: 80px 80px;
  pointer-events: none;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.1); }
}

// Header
.slide-header {
  text-align: center;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
}

.ai-badge-header {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  background: rgba(124, 58, 237, 0.15);
  border: 1px solid rgba(124, 58, 237, 0.3);
  border-radius: 100px;
  margin-bottom: 20px;
}

.ai-sparkle {
  color: #a78bfa;
  animation: sparkle 2s ease-in-out infinite;
}

@keyframes sparkle {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

.ai-badge-header span {
  font-family: $font-mono;
  font-size: 11px;
  font-weight: 600;
  color: #a78bfa;
  letter-spacing: 0.15em;
}

.slide-title {
  font-family: $font-display;
  font-style: italic;
  font-size: 56px;
  color: #fff;
  margin: 0 0 12px;
  letter-spacing: -0.02em;
}

.slide-subtitle {
  font-family: $font-body;
  font-size: 18px;
  color: rgba(255,255,255,0.6);
  margin: 0;
}

// Recommendations Grid
.recommendations-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  position: relative;
  z-index: 1;
}

.rec-card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 20px;
  padding: 36px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  animation: cardSlideUp 0.6s $ease-out-expo backwards;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255,255,255,0.05);
    border-color: rgba(255,255,255,0.12);
    transform: translateY(-4px);
  }

  &.priority-high {
    border-color: rgba(239, 68, 68, 0.3);

    &:hover {
      border-color: rgba(239, 68, 68, 0.5);
    }
  }

  &.priority-medium {
    border-color: rgba(245, 158, 11, 0.3);

    &:hover {
      border-color: rgba(245, 158, 11, 0.5);
    }
  }
}

@keyframes cardSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.priority-ribbon {
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 6px 14px;
  border-radius: 6px;
  font-family: $font-mono;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;

  &.ribbon-high {
    background: rgba(239, 68, 68, 0.2);
    color: #fca5a5;
  }

  &.ribbon-medium {
    background: rgba(245, 158, 11, 0.2);
    color: #fcd34d;
  }

  &.ribbon-low {
    background: rgba(34, 197, 94, 0.2);
    color: #86efac;
  }
}

.card-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(124, 58, 237, 0.15);
  color: #a78bfa;

  svg {
    width: 28px;
    height: 28px;
  }

  &.icon-wellbeing {
    background: rgba(239, 68, 68, 0.15);
    color: #fca5a5;
  }

  &.icon-engagement {
    background: rgba(124, 58, 237, 0.15);
    color: #a78bfa;
  }

  &.icon-culture {
    background: rgba(34, 197, 94, 0.15);
    color: #86efac;
  }

  &.icon-leadership {
    background: rgba(245, 158, 11, 0.15);
    color: #fcd34d;
  }
}

.card-body {
  flex: 1;
}

.card-category {
  font-family: $font-mono;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255,255,255,0.5);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 10px;
}

.card-title {
  font-family: $font-body;
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 14px;
  line-height: 1.25;
}

.card-description {
  font-family: $font-body;
  font-size: 17px;
  color: rgba(255,255,255,0.7);
  line-height: 1.55;
  margin: 0;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.08);
}

.impact-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: $font-body;
  font-size: 14px;
  color: rgba(255,255,255,0.6);

  svg {
    width: 16px;
    height: 16px;
    color: #a78bfa;
  }
}

.metric-pill {
  padding: 6px 14px;
  background: rgba(255,255,255,0.08);
  border-radius: 8px;
  font-family: $font-mono;
  font-size: 13px;
  color: rgba(255,255,255,0.7);
}

// Footer
.slide-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.06);
  position: relative;
  z-index: 1;
}

.ai-signature {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: $font-body;
  font-size: 13px;
  color: rgba(255,255,255,0.5);
}

.signature-icon {
  color: rgba(124, 58, 237, 0.6);
}

.timestamp {
  font-family: $font-mono;
  font-size: 12px;
  color: rgba(255,255,255,0.3);
}
</style>
